import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtMoney, fmtNumber } from '../lib/format';
import { Button, Callout, Checkbox, Field as FormField, Input, Ltr, Select, useToast } from '../components/ui';
import { IconUpload } from '../components/icons';

type Field = 'name' | 'price' | 'cost' | 'description' | 'sku';
type CostChange = 'new' | 'up' | 'down' | 'same' | null;
type Row = {
  rowNumber: number;
  name: string;
  description: string | null;
  sku: string | null;
  price: number;
  /** only for users with the profit permission */
  cost: number | null;
  costChange: CostChange;
  action: 'update' | 'new_price' | 'same' | 'not_in_catalog' | 'error';
  errors: string[];
  warnings: string[];
  products: Array<{ id: number; sku: string; name: string; oldPrice: number | null; oldCost?: number | null }>;
  variants: string[];
  suggestion: { sku: string; name: string; score: number } | null;
};
type Preview = {
  headers: string[];
  mapping: Record<string, Field | null>;
  rows: Row[];
  needsMapping: boolean;
  vatInHeader: boolean;
  fields: Field[];
  hasCost?: boolean;
  /** the file has costs, but this user may not see them (they are not imported) */
  costsHidden?: boolean;
  fileId?: number;
  fileName?: string;
  summary: {
    update: number;
    newPrice: number;
    same: number;
    notInCatalog: number;
    error: number;
    catalogSize: number;
    costs: { withCost: number; new: number; up: number; down: number } | null;
  } | null;
};
type Result = { saved: number; removed: number; updated: number; costsUpdated?: number; linked: number; unmatched: Array<{ name: string; description: string | null; sku: string | null; price: number }> };

const FIELD_LABELS: Record<Field, string> = { name: 'שם מוצר', price: 'מחיר', cost: 'עלות (לא חובה)', description: 'תיאור — צבע / גודל (לא חובה)', sku: 'מק״ט (לא חובה)' };
const ACTION = {
  update: { label: 'מחיר מתעדכן', tone: 'info' },
  new_price: { label: 'מחיר חדש למוצר', tone: 'ok' },
  same: { label: 'ללא שינוי', tone: '' },
  not_in_catalog: { label: 'לא נמצא בקטלוג', tone: 'warn' },
  error: { label: 'שגיאה', tone: 'bad' },
} as const;

/** The price file: product name + price, linked to the catalog by name. */
export function PriceImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [includesVat, setIncludesVat] = useState(false);
  const [costIncludesVat, setCostIncludesVat] = useState(false);
  const [costFrom, setCostFrom] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' }));
  const [replaceAll, setReplaceAll] = useState(false);
  const [filter, setFilter] = useState<'changes' | 'not_in_catalog' | 'all'>('changes');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const runPreview = async (f: File, opts: { mapping?: Record<string, Field | null>; vat?: boolean; costVat?: boolean } = {}) => {
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('includesVat', String(opts.vat ?? includesVat));
      form.append('costIncludesVat', String(opts.costVat ?? costIncludesVat));
      if (opts.mapping) form.append('mapping', JSON.stringify(opts.mapping));
      form.append('file', f);
      setPreview(await api<Preview>('/api/products/prices/preview', { form }));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const valid = preview?.rows.filter((r) => r.action !== 'error') ?? [];
  const commit = async () => {
    setBusy(true);
    try {
      const rows = valid.map(({ rowNumber, name, description, sku, price, cost }) => ({ rowNumber, name, description, sku, price, cost: cost ?? null }));
      const body = { rows, replaceAll, costFrom: costs ? costFrom : null, fileId: preview?.fileId ?? null, fileName: preview?.fileName ?? null };
      setResult(await api<Result>('/api/products/prices/commit', { body }));
      for (const k of ['products', 'price-list']) qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const shown =
    preview?.rows.filter((r) =>
      filter === 'all' ? true : filter === 'not_in_catalog' ? r.action === 'not_in_catalog' : r.action !== 'same' && r.action !== 'not_in_catalog',
    ) ?? [];
  const s = preview?.summary;
  // Costs are shown only when the server sent them (profit permission).
  const costs = Boolean(preview?.hasCost && !preview.costsHidden);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/products">קטלוג מוצרים</Link></div>
          <h1>ייבוא קובץ מחירים</h1>
          <div className="sub">
            CSV או Excel עם שם מוצר ומחיר. המחיר מתחבר למוצר בקטלוג לפי השם (רווחים, גרשיים וסימנים לא משנים).
            למוצר עם כמה צבעים / גדלים: שורה עם שם בלבד מתמחרת את כל הווריאציות; שורה עם <strong>תיאור</strong> (כמו בקטלוג — "כחול", "גב 8 (גדול)") או מק״ט מתמחרת רק את הווריאציה הזו וגוברת.
            אפשר להעלות לפני הקטלוג או אחריו: שמות שעוד אין בקטלוג נשמרים ויקבלו את המחיר כשהמוצר ייווסף.
          </div>
        </div>
      </div>

      {result ? (
        <div className="panel panel-body stack">
          <Callout tone="ok" title="המחירים נשמרו">
            {fmtNumber(result.saved)} מחירים בקובץ · {fmtNumber(result.updated)} מוצרים בקטלוג קיבלו מחיר חדש · {fmtNumber(result.linked)} מחוברים למוצר
            {result.costsUpdated ? ` · ${fmtNumber(result.costsUpdated)} עלויות עודכנו` : ''}
            {result.removed ? ` · ${fmtNumber(result.removed)} מחירים ישנים שלא בקובץ הוסרו` : ''}
          </Callout>
          {result.unmatched.length ? (
            <Callout tone="warn" title={`${fmtNumber(result.unmatched.length)} שמות לא נמצאו בקטלוג`}>
              המחיר שלהם נשמר ויתחבר אוטומטית כשיהיה מוצר עם אותו שם. הרשימה המלאה במסך הקטלוג.
            </Callout>
          ) : null}
          <div className="row">
            <Link to="/products" className="btn primary">לקטלוג</Link>
          </div>
        </div>
      ) : (
        <div className="stack">
          <div className="panel panel-body stack">
            <div className="dropzone" onClick={() => input.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}>
              {busy && !preview ? (
                <div className="row" style={{ justifyContent: 'center' }}><span className="spinner" /> קורא את הקובץ…</div>
              ) : (
                <>
                  <IconUpload size={22} />
                  <div className="strong" style={{ marginTop: 8 }}>{file ? file.name : 'בחירת קובץ מחירים (Excel או CSV)'}</div>
                  <div className="small muted">{file ? 'לחצו להחלפת קובץ' : 'העמודות יזוהו אוטומטית — ניתן לתקן את ההתאמה'}</div>
                </>
              )}
            </div>
            <input
              ref={input}
              type="file"
              hidden
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setFile(f);
                  void runPreview(f);
                }
                e.target.value = '';
              }}
            />
            <Checkbox
              checked={includesVat}
              onChange={(v) => {
                setIncludesVat(v);
                if (file && preview) void runPreview(file, { mapping: preview.mapping, vat: v });
              }}
            >
              המחירים בקובץ כוללים מע״מ (יומרו למחיר לפני מע״מ, כמו בשורות המסמכים)
            </Checkbox>
            {preview?.vatInHeader && !includesVat ? <Callout tone="warn">כותרת עמודת המחיר מציינת "כולל מע״מ" — אם כך, סמנו את התיבה למעלה.</Callout> : null}
            {costs ? (
              <>
                <Checkbox
                  checked={costIncludesVat}
                  onChange={(v) => {
                    setCostIncludesVat(v);
                    if (file && preview) void runPreview(file, { mapping: preview.mapping, costVat: v });
                  }}
                >
                  העלויות בקובץ כוללות מע״מ (יומרו לעלות לפני מע״מ)
                </Checkbox>
                <FormField label="העלויות בתוקף מתאריך" hint="עלות קודמת נשמרת בהיסטוריה עד התאריך הזה">
                  <Input type="date" value={costFrom} onChange={(e) => setCostFrom(e.target.value)} style={{ maxWidth: 200 }} ltr />
                </FormField>
              </>
            ) : null}
            {preview?.costsHidden ? <Callout>בקובץ יש עמודת עלות — היא לא תיובא (נדרשת הרשאת "רווחיות ועלויות"). העלויות הקיימות לא ישתנו.</Callout> : null}
          </div>

          {preview ? (
            <>
              <section className="panel">
                <div className="panel-head"><h2>התאמת עמודות</h2></div>
                <div className="panel-body">
                  <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                    {preview.headers.map((h) => (
                      <label key={h} className="field">
                        <span className="field-label ellipsis" title={h}>{h}</span>
                        <Select
                          value={preview.mapping[h] ?? ''}
                          onChange={(e) => {
                            const next = { ...preview.mapping };
                            const val = (e.target.value || null) as Field | null;
                            if (val) for (const k of Object.keys(next)) if (next[k] === val) next[k] = null;
                            next[h] = val;
                            if (file) void runPreview(file, { mapping: next });
                          }}
                        >
                          <option value="">לא לייבא</option>
                          {preview.fields.map((f) => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                        </Select>
                      </label>
                    ))}
                  </div>
                  {preview.needsMapping ? <Callout tone="warn">יש לבחור את העמודות של שם המוצר והמחיר.</Callout> : null}
                </div>
              </section>

              {s ? (
                <section className="panel">
                  <div className="panel-head row wrap">
                    <h2>{fmtNumber(preview.rows.length)} מחירים</h2>
                    <span className="badge info">{fmtNumber(s.update)} מתעדכנים</span>
                    <span className="badge ok">{fmtNumber(s.newPrice)} מחיר חדש למוצר</span>
                    <span className="badge">{fmtNumber(s.same)} ללא שינוי</span>
                    {s.notInCatalog ? <span className="badge warn">{fmtNumber(s.notInCatalog)} לא נמצאו בקטלוג</span> : null}
                    {s.error ? <span className="badge bad">{fmtNumber(s.error)} שגיאות (לא ייובאו)</span> : null}
                    {costs && s.costs ? (
                      <span className="small muted">
                        עלויות: {fmtNumber(s.costs.withCost)} מוצרים · {fmtNumber(s.costs.new)} חדשות · {fmtNumber(s.costs.up)} עלו · {fmtNumber(s.costs.down)} ירדו
                      </span>
                    ) : null}
                    <span className="spacer" />
                    <Select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)} style={{ width: 'auto' }} aria-label="סינון">
                      <option value="changes">שינויים ושגיאות</option>
                      <option value="not_in_catalog">לא נמצאו בקטלוג</option>
                      <option value="all">הכל</option>
                    </Select>
                  </div>
                  {s.catalogSize === 0 ? (
                    <div className="panel-body" style={{ paddingBottom: 0 }}>
                      <Callout>הקטלוג עדיין ריק — המחירים יישמרו ויתחברו למוצרים מיד כשתעלו את קובץ הקטלוג.</Callout>
                    </div>
                  ) : null}
                  <div className="table-wrap" style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>שורה</th>
                          <th>שם בקובץ המחירים</th>
                          <th className="num">מחיר לפני מע״מ</th>
                          {costs ? <th className="num">עלות</th> : null}
                          <th>מוצר בקטלוג</th>
                          <th>פעולה</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((r) => (
                          <tr key={r.rowNumber} style={r.action === 'error' ? { background: 'var(--surface-2)' } : undefined}>
                            <td className="muted tabular">{r.rowNumber}</td>
                            <td className="primary">
                              {r.name || <span className="faint">—</span>}
                              {r.description || r.sku ? (
                                <span className="sub">
                                  {r.description ? <span className="badge" style={{ marginInlineEnd: 6 }}>{r.description}</span> : null}
                                  {r.sku ? <Ltr>{r.sku}</Ltr> : null}
                                </span>
                              ) : null}
                            </td>
                            <td className="num tabular">{r.action === 'error' && !r.price ? <span className="faint">—</span> : fmtMoney(r.price)}</td>
                            {costs ? (
                              <td className="num tabular">
                                {r.cost == null ? <span className="faint">—</span> : fmtMoney(r.cost)}
                                {r.costChange === 'up' || r.costChange === 'down' ? (
                                  <span className="sub" style={{ color: r.costChange === 'up' ? 'var(--bad)' : 'var(--ok)' }}>
                                    {r.costChange === 'up' ? '▲' : '▼'} היה {fmtMoney(r.products[0]?.oldCost ?? 0)}
                                  </span>
                                ) : r.costChange === 'new' ? <span className="sub">עלות ראשונה</span> : null}
                              </td>
                            ) : null}
                            <td className="small">
                              {r.products.map((p) => (
                                <div key={p.id}>
                                  <Ltr className="muted">{p.sku}</Ltr> {p.name !== r.name ? p.name : ''}
                                  {r.action === 'update' && p.oldPrice != null ? <span className="muted"> · היה {fmtMoney(p.oldPrice)}</span> : null}
                                </div>
                              ))}
                              {r.products.length > 1 ? <div className="faint">{r.products.length} וריאציות</div> : null}
                              {r.variants.length ? <div className="muted">השם קיים, אבל בלי {r.sku ? 'המק״ט' : 'התיאור'} הזה. בקטלוג: {r.variants.join(' · ')}</div> : null}
                              {r.suggestion ? <div className="muted">הכי דומה: {r.suggestion.name} ({Math.round(r.suggestion.score * 100)}%)</div> : null}
                              {!r.products.length && !r.suggestion && !r.variants.length && r.action !== 'error' ? <span className="faint">—</span> : null}
                            </td>
                            <td className="small" style={{ maxWidth: 260 }}>
                              <span className={`badge ${ACTION[r.action].tone}`}>{ACTION[r.action].label}</span>
                              {r.errors.map((e) => <div key={e} style={{ color: 'var(--bad)' }}>{e}</div>)}
                              {r.warnings.map((w) => <div key={w} style={{ color: 'var(--warn)' }}>{w}</div>)}
                            </td>
                          </tr>
                        ))}
                        {shown.length === 0 ? <tr><td colSpan={costs ? 6 : 5} className="muted">אין שורות בסינון הזה.</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                  <div className="panel-foot stack" style={{ gap: 10 }}>
                    <Checkbox checked={replaceAll} onChange={setReplaceAll}>
                      זה מחירון מלא — להסיר מחירים ישנים שהועלו בעבר ואינם בקובץ הזה (המחירים במוצרים עצמם לא נמחקים)
                    </Checkbox>
                    <div className="row wrap">
                      <Button variant="primary" onClick={commit} loading={busy} disabled={valid.length === 0}>
                        שמירת {fmtNumber(valid.length)} מחירים
                      </Button>
                      <span className="small muted">המחירים יתחברו מיד למוצרים בקטלוג עם אותו שם.</span>
                    </div>
                  </div>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </>
  );
}
